import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Message,
  Popconfirm,
  Select,
  Table,
  Typography,
} from "@arco-design/web-react";
import { Grid } from "@arco-design/web-react";
import axios from "axios";
import { endpoints } from "../api/endpoints";
import { trackEvent } from "../analytics/track";
import { useEffect, useMemo, useState } from "react";
import { getButtonColor, getButtonTextColor } from "../utils/cssVariables";
import {
  AVAILABILITY_DATE_INPUT_FORMAT,
  buildAvailabilityHourOptions,
  formatAvailabilityDateForApi,
  formatAvailabilityDateRange,
  formatAvailabilityHourLabel,
  formatTimezoneLabel,
  hourFromTimeField,
} from "../utils/formatAvailabilityDate";
import { useBreakpoint } from "../utils/hooks/useBreakpoint";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import "./MentorSettings.css";

const Row = Grid.Row;
const Col = Grid.Col;
const FormItem = Form.Item;

const defaultSlotFormValues = {
  weekdays: [1, 2, 3, 4, 5],
  fromHour: 8,
  toHour: 17,
};

export const MentorSettings = () => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint("mobile");
  const [form] = Form.useForm();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hourOptions = useMemo(() => buildAvailabilityHourOptions(), []);

  const onSubmit = async () => {
    if (form) {
      console.log(form.getFields());
      form.validate().then(() => {
        const fields = form.getFields();

        if (!fields.date || !fields.date[0] || !fields.date[1]) {
          Message.error(t('notification_messages.error_no_selected_day'));
          return;
        }

        if (fields.fromHour == null || fields.toHour == null) {
          Message.error(t('notification_messages.error_no_selected_time'));
          return;
        }

        if (!fields.weekdays || fields.weekdays.length === 0) {
          Message.error(t('notification_messages.error_no_selected_weekday'));
          return;
        }

        let data = {
          from_date: formatAvailabilityDateForApi(fields.date[0]),
          to_date: formatAvailabilityDateForApi(fields.date[1]),
          from_time: hourFromTimeField(fields.fromHour),
          to_time: hourFromTimeField(fields.toHour),
          weekdays: fields.weekdays,
          timezone: timezone,
          active: true,
        } as any;

        if (!data.weekdays) {
          data.weekdays = [1, 2, 3, 4, 5];
        }

        axios
          .post(endpoints.addSlot, data)
          .then((response) => {
            Message.success({ content: t('notification_messages.slots_added_success'), closable: true, duration: 3000});
            trackEvent("availability_set");
            loadSlots();
            form.resetFields();
            form.setFieldsValue(defaultSlotFormValues);
          })
          .catch((error) => {
            console.error(error);
            Message.error(t('notification_messages.slots_added_error'));
          })
          .finally(() => {});
      });
    }
  };

  const [allSlots, setAllSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSlots = async () => {
    setLoading(true);
    axios
      .get(endpoints.getSlots)
      .then((response) => {
        setAllSlots(response.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadSlots();
    form.setFieldsValue(defaultSlotFormValues);
    trackEvent("availability_view");
  }, [form]);

  const disabledPastDate = (current: dayjs.Dayjs) =>
    current.isBefore(dayjs().subtract(1, "day"));

  const mobileDateFields = (
    <div className="mentor-settings-page__date-range-fields">
      <FormItem
        field="dateStart"
        layout="vertical"
        label={t("availability_page.start_date")}
        rules={[{ required: true }]}
      >
        <DatePicker
          style={{ width: "100%" }}
          format={AVAILABILITY_DATE_INPUT_FORMAT}
          disabledDate={disabledPastDate}
          onChange={(_, date) => {
            form.setFieldValue("date", [date, form.getFieldValue("dateEnd")]);
          }}
        />
      </FormItem>
      <FormItem
        field="dateEnd"
        layout="vertical"
        label={t("availability_page.end_date")}
        rules={[{ required: true }]}
      >
        <DatePicker
          style={{ width: "100%" }}
          format={AVAILABILITY_DATE_INPUT_FORMAT}
          disabledDate={disabledPastDate}
          onChange={(_, date) => {
            form.setFieldValue("date", [form.getFieldValue("dateStart"), date]);
          }}
        />
      </FormItem>
      <FormItem field="date" hidden rules={[{ required: true }]}>
        <Input type="hidden" />
      </FormItem>
    </div>
  );

  const dayToAbbrev = (day: number) => {
    switch (day) {
      case 0:
        return t('weekdays.sunday_short');
      case 1:
        return t('weekdays.monday_short');
      case 2:
        return t('weekdays.tuesday_short');
      case 3:
        return t('weekdays.wednesday_short');
      case 4:
        return t('weekdays.thursday_short');
      case 5:
        return t('weekdays.friday_short');
      case 6:
        return t('weekdays.saturday_short');
      default:
        return "Unknown";
    }
  };

  return (
    <div className="mentor-settings-page">
      <Card
        style={{
          width: "100%",
        }}
        className="card-with-icon-hover own-card"
      >
        <Typography.Title
          heading={3}
          style={{
            textAlign: "left",
            margin: "10px 0 20px 0",
            wordBreak: "break-word",
            textTransform: "uppercase",
          }}
        >
          {t('availability_page.your_slots')}
        </Typography.Title>

        {(!!allSlots && allSlots.length > 0 && (
          <Table
            className="mentor-settings-page__slots-table"
            data={allSlots}
            loading={loading}
            rowKey="id"
            pagination={false}
            style={{ width: "100%" }}
            scroll={isMobile ? { x: 640 } : undefined}
            tableLayoutFixed={!isMobile}
            columns={[
              {
                title: t('availability_page.dates'),
                dataIndex: "from_date",
                key: "from_date",
                render: (text, record: any) => (
                  <Typography.Text style={{ wordBreak: "break-word" }}>
                    {formatAvailabilityDateRange(record.from_date, record.to_date)}
                  </Typography.Text>
                ),
                width: isMobile ? 140 : 170,
              },
              {
                title: t('availability_page.hours'),
                dataIndex: "from_time",
                key: "from_time",
                render: (text, record: any) => (
                  <Typography.Text style={{ wordBreak: "break-word" }}>
                    {formatAvailabilityHourLabel(record.from_time)} -{" "}
                    {formatAvailabilityHourLabel(record.to_time)}
                  </Typography.Text>
                ),
                width: 110,
              },
              {
                title: t('availability_page.days'),
                dataIndex: "weekdays",
                key: "weekdays",
                render: (text, record: any) => (
                  <Typography.Text style={{ wordBreak: "break-word" }}>
                    {!!record.weekdays &&
                      record.weekdays.map((day: number) => (
                        <span key={day}>{dayToAbbrev(day)} </span>
                      ))}
                  </Typography.Text>
                ),
                width: 120,
              },
              {
                title: t('availability_page.timezone'),
                dataIndex: "timezone",
                key: "timezone",
                width: 110,
                render: (text, record: any) => (
                  <Typography.Text style={{ wordBreak: "break-word" }}>
                    {formatTimezoneLabel(record.timezone)}
                  </Typography.Text>
                ),
              },
              {
                title: t('availability_page.actions'),
                dataIndex: "id",
                key: "id",
                render: (text, record: any) => (
                  <Popconfirm
                    title={t('availability_page.delete_slot_confirmation_message')}
                    onOk={() => {
                      axios
                        .delete(`${endpoints.deleteSlot}${record.id}`)
                        .then((response) => {
                          loadSlots();
                          Message.success({content: t('notification_messages.slot_deleted_success'), closable: true, duration: 3000});
                        })
                        .catch((error) => {
                          console.error(error);
                          Message.error(t('notification_messages.slot_deleted_error'));
                        })
                        .finally(() => {});
                    }}
                    okText={t('common.confirm_delete_button')}
                    cancelText={t('common.decline_cancel_button')}
                    okButtonProps={{
                      size: "large",
                      style: {
                        color: getButtonTextColor(),
                        backgroundColor: getButtonColor(),
                      },
                    }}
                    cancelButtonProps={{
                      size: "large",
                      style: {
                        color: getButtonTextColor(),
                        backgroundColor: getButtonColor(),
                      },
                    }}
                  >
                    <Button size={"small"}>{t('common.delete_button')}</Button>
                  </Popconfirm>
                ),
                width: 100,
              },
            ]}
          />
        )) || (
          <Typography.Text style={{ textAlign: "center", width: "100%" }}>
            {t('availability_page.no_slots_added_message')}
          </Typography.Text>
        )}
      </Card>

      <Card
        style={{
          width: "100%",
          marginTop: 20,
        }}
        className="card-with-icon-hover own-card"
      >
        <Typography.Title
          heading={3}
          style={{
            textAlign: "left",
            margin: "10px 0 20px 0",
            wordBreak: "break-word",
            textTransform: "uppercase",
          }}
        >
          {t('availability_page.add_slots_heading')}
        </Typography.Title>
        <Form onSubmit={onSubmit} form={form} initialValues={defaultSlotFormValues}>
          <Row justify="center">
            <Col span={24} style={{ margin: 20, padding: 10 }}>
              <Row justify="center">
                <Col
                  span={24}
                  xl={{ span: 14 }}
                  xs={{ span: 24 }}
                  md={{ span: 18 }}
                >
                  {isMobile ? (
                    mobileDateFields
                  ) : (
                    <FormItem
                      id="date"
                      field="date"
                      layout="vertical"
                      label={t('availability_page.select_date_label')}
                      rules={[{ required: true }]}
                    >
                      <DatePicker.RangePicker
                        style={{ width: "100%" }}
                        format={AVAILABILITY_DATE_INPUT_FORMAT}
                        disabledDate={disabledPastDate}
                      />
                    </FormItem>
                  )}

                  <FormItem
                    id="weekdays"
                    field="weekdays"
                    layout="vertical"
                    label={t('availability_page.select_weekdays_label')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      mode="multiple"
                      placeholder={t('availability_page.select_weekdays_placeholder')}
                    >
                      <Select.Option value={0}>{t('weekdays.sunday')}</Select.Option>
                      <Select.Option value={1}>{t('weekdays.monday')}</Select.Option>
                      <Select.Option value={2}>{t('weekdays.tuesday')}</Select.Option>
                      <Select.Option value={3}>{t('weekdays.wednesday')}</Select.Option>
                      <Select.Option value={4}>{t('weekdays.thursday')}</Select.Option>
                      <Select.Option value={5}>{t('weekdays.friday')}</Select.Option>
                      <Select.Option value={6}>{t('weekdays.saturday')}</Select.Option>
                    </Select>
                  </FormItem>

                  <FormItem
                    label={t('availability_page.timezone_label')}
                    layout="vertical"
                    rules={[{ required: true }]}
                  >
                    <Input disabled value={formatTimezoneLabel(timezone)} />
                  </FormItem>

                  <Row gutter={12}>
                    <Col span={12}>
                      <FormItem
                        id="fromHour"
                        field="fromHour"
                        layout="vertical"
                        label={t('availability_page.start_time')}
                        rules={[{ required: true }]}
                      >
                        <Select options={hourOptions} />
                      </FormItem>
                    </Col>
                    <Col span={12}>
                      <FormItem
                        id="toHour"
                        field="toHour"
                        layout="vertical"
                        label={t('availability_page.end_time')}
                        rules={[{ required: true }]}
                      >
                        <Select options={hourOptions} />
                      </FormItem>
                    </Col>
                  </Row>

                  <Row justify="center" style={{ marginTop: 10 }}>
                    <Col span={24}>
                      <Button
                        htmlType="submit"
                        style={{
                          color: getButtonTextColor(),
                          backgroundColor: getButtonColor(),
                          width: "100%",
                        }}
                      >
                        {t('availability_page.add_slot_button')}
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};
